import { searchClient } from '@algolia/client-search';
import invariant from 'invariant';
import queryString from 'query-string';

import { configValue } from '#/config/appConfigUtils';
import { RichError } from '#/utils/RichError';
import { getJson } from '#/utils/http/getJson';
import { trackError } from '#/utils/trackError';

import {
  AlgoliaValidUntilError,
  isValidUntilError,
} from './AlgoliaValidUntilError';

type Args = {
  config: Readonly<{ appId: string; apiKey: string }>;
  index: string;
  id: string;
  attributes: string[];
};

export async function algoliaGetObject<T>({
  config: { appId, apiKey },
  index,
  id,
  attributes,
}: Args): Promise<T | void> {
  if (configValue('algolia', 'mock')) {
    return getJson<{ object: T }>(
      `http://algoliamock:6577/${index}/objects/${id}`,
    ).then((response) => {
      if (response.object !== null) {
        return response.object;
      }
      throw new Error('Object not found');
    });
  }
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');
  const client = searchClient(appId, apiKey);

  return client
    .getObject({
      indexName: index,
      objectID: id,
      attributesToRetrieve: attributes,
    })
    .then((result) => result as T)
    .catch((e) => {
      if (isValidUntilError(e)) {
        throw new AlgoliaValidUntilError();
      }

      trackError(
        new RichError('Algolia POST request error.', {
          errorMessage: e.message,
          requestParams: queryString.stringify({
            id,
            attributes,
          }),
          statusCode: e.statusCode,
          response: e.response,
        }),
      );
    });
}
